import { useSyncExternalStore, type ReactNode } from 'react';
import { BLEND_MODE_GROUPS, BLEND_MODE_LABELS } from '../core/blendModes';
import { COLOR_CHANNEL_NAMES, findChannel } from '../core/channels';
import { hexToRgba, rgbaToCss, rgbaToHex } from '../core/color';
import { GRADIENT_TYPES, GRADIENT_TYPE_LABELS, gradientToCss } from '../core/gradient';
import { TONE_RANGE_LABELS } from '../core/paintFilters';
import { TEXT_ALIGN_OPTIONS, TEXT_FONT_OPTIONS } from '../core/textFonts';
import {
  isPaintTool,
  TRANSFORM_ANCHORS,
  type BlendMode,
  type PaintToolId,
  type SelectionCombineMode,
  type TextAlign,
  type ToolId,
  type TransformMode,
  type VectorMode,
  type RGBA,
} from '../core/types';
import { getTextEditSession, subscribeTextEdit } from '../state/textEdit';
import { setTextLayerProps, updateTextTransient } from '../state/textActions';
import { findLayerById } from '../core/layerTree';
import type { PathOp, StrokeAlign } from '../vector/types';
import {
  applyTransformValues,
  cancelTransformSession,
  commitTransformSession,
  setTransformMode,
  setWarpPreset,
  subscribeTransform,
  syncTransformPivot,
  transformRevision,
  transformValues,
  warpSettings,
  type TransformValues,
} from '../tools/transform';
import { WARP_PRESETS, WARP_PRESET_LABELS, type WarpPreset } from '../core/warp';
import { getActiveDocument, getPaintOptions, useAppStore, type AppState } from '../state/store';
import { TOOLS, TOOL_LABELS } from '../tools';
import { Checkbox, NumberInput, Select } from './components/controls';

const SELECTION_MODES: { value: SelectionCombineMode; label: string }[] = [
  { value: 'replace', label: 'New' },
  { value: 'add', label: 'Add' },
  { value: 'subtract', label: 'Subtract' },
  { value: 'intersect', label: 'Intersect' },
  { value: 'exclude', label: 'Exclude' },
];

/** Tools that combine a new shape or region with the current selection. */
const SELECTION_TOOLS = new Set<ToolId>([
  'selectRect',
  'selectEllipse',
  'lasso',
  'lassoPolygon',
  'lassoMagnetic',
  'magicWand',
  'quickSelect',
]);

/** Tools that draw vector geometry and therefore share the Path/Shape/Pixels modes. */
const VECTOR_TOOLS = new Set<ToolId>([
  'pen',
  'penFree',
  'shapeRect',
  'shapeEllipse',
  'shapeLine',
  'shapePolygon',
]);

const VECTOR_MODES: { value: VectorMode; label: string }[] = [
  { value: 'shape', label: 'Shape' },
  { value: 'path', label: 'Path' },
  { value: 'pixels', label: 'Pixels' },
];

const PATH_OPS: { value: PathOp; label: string }[] = [
  { value: 'add', label: 'Combine' },
  { value: 'subtract', label: 'Subtract Front' },
  { value: 'intersect', label: 'Intersect' },
  { value: 'exclude', label: 'Exclude Overlap' },
];

const TRANSFORM_MODE_OPTIONS: { value: TransformMode; label: string }[] = [
  { value: 'free', label: 'Free' },
  { value: 'skew', label: 'Skew' },
  { value: 'distort', label: 'Distort' },
  { value: 'perspective', label: 'Perspective' },
  { value: 'warp', label: 'Warp' },
];

const WARP_PRESET_OPTIONS: { value: WarpPreset; label: string }[] = WARP_PRESETS.map((preset) => ({
  value: preset,
  label: WARP_PRESET_LABELS[preset],
}));

const STROKE_ALIGNS: { value: StrokeAlign; label: string }[] = [
  { value: 'inside', label: 'Inside' },
  { value: 'center', label: 'Center' },
  { value: 'outside', label: 'Outside' },
];

const BLEND_MODE_OPTIONS: { value: BlendMode; label: string }[] = BLEND_MODE_GROUPS.flat().map(
  (mode) => ({ value: mode, label: BLEND_MODE_LABELS[mode] }),
);

const PAINT_TARGETS = [
  { value: 'layer' as const, label: 'Layer' },
  { value: 'mask' as const, label: 'Mask' },
];

const TONE_RANGES = (['shadows', 'midtones', 'highlights'] as const).map((range) => ({
  value: range,
  label: TONE_RANGE_LABELS[range],
}));

const SPONGE_MODES = [
  { value: 'saturate' as const, label: 'Saturate' },
  { value: 'desaturate' as const, label: 'Desaturate' },
];

const GRADIENT_TYPE_OPTIONS = GRADIENT_TYPES.map((type) => ({
  value: type,
  label: GRADIENT_TYPE_LABELS[type],
}));

/** Which extra controls each brush-driven tool needs. */
const COLOR_TOOLS = new Set<PaintToolId>(['brush', 'pencil', 'eraser']);
const BLEND_TOOLS = new Set<PaintToolId>(['brush', 'pencil']);
const TRANSFER_TOOLS = new Set<PaintToolId>(['brush', 'pencil', 'eraser', 'clone']);
const STRENGTH_TOOLS = new Set<PaintToolId>(['blur', 'sharpen', 'smudge', 'sponge']);
const EXPOSURE_TOOLS = new Set<PaintToolId>(['dodge', 'burn']);
const SOURCE_TOOLS = new Set<PaintToolId>(['clone', 'healing']);

/**
 * Name of the channel a stroke would land in, or null when the layer itself is
 * the target. A selected channel overrides the "Paint on" choice, so saying so
 * is clearer than leaving a control that no longer decides anything.
 */
function activeChannelName(state: AppState): string | null {
  const channel = state.activeChannel;
  if (channel.kind === 'color') return `the ${COLOR_CHANNEL_NAMES[channel.index]} channel`;
  if (channel.kind !== 'alpha') return null;
  const doc = getActiveDocument(state);
  return doc ? (findChannel(doc, channel.id)?.name ?? null) : null;
}

/**
 * Photoshop-style options bar: size / hardness / mode / opacity / flow up top,
 * with tip shape, spacing and dynamics left in the Brush panel.
 */
function PaintOptionsRow() {
  const tool = useAppStore((state) => state.tool);
  const options = useAppStore((state) => getPaintOptions(state));
  const patch = useAppStore((state) => state.patchPaintOptions);
  const paintTarget = useAppStore((state) => state.paintTarget);
  const setPaintTarget = useAppStore((state) => state.setPaintTarget);
  const channelName = useAppStore(activeChannelName);
  if (!isPaintTool(tool)) return null;

  const percent = (value: number): number => Math.round(value * 100);

  return (
    <>
      <Labeled label="Size">
        <NumberInput
          value={Math.round(options.size)}
          min={1}
          max={5000}
          width={56}
          onChange={(size) => patch(tool, { size })}
        />
      </Labeled>
      {options.tipImageId === null && (
        <Labeled label="Hardness">
          <NumberInput
            value={percent(options.hardness)}
            min={0}
            max={100}
            width={48}
            onChange={(value) => patch(tool, { hardness: value / 100 })}
          />
        </Labeled>
      )}
      {BLEND_TOOLS.has(tool) && (
        <Labeled label="Mode">
          <Select
            value={options.blendMode}
            options={BLEND_MODE_OPTIONS}
            onChange={(blendMode) => patch(tool, { blendMode })}
          />
        </Labeled>
      )}
      {TRANSFER_TOOLS.has(tool) && (
        <>
          <Labeled label="Opacity">
            <input
              className="slider optionsbar__slider"
              type="range"
              min={1}
              max={100}
              value={percent(options.opacity)}
              onChange={(event) =>
                patch(tool, { opacity: Math.max(0.01, Number.parseInt(event.target.value, 10) / 100) })
              }
            />
            <NumberInput
              value={percent(options.opacity)}
              min={1}
              max={100}
              width={48}
              onChange={(value) => patch(tool, { opacity: Math.max(0.01, value / 100) })}
            />
          </Labeled>
          <Labeled label="Flow">
            <input
              className="slider optionsbar__slider"
              type="range"
              min={1}
              max={100}
              value={percent(options.flow)}
              onChange={(event) =>
                patch(tool, { flow: Math.max(0.01, Number.parseInt(event.target.value, 10) / 100) })
              }
            />
            <NumberInput
              value={percent(options.flow)}
              min={1}
              max={100}
              width={48}
              onChange={(value) => patch(tool, { flow: Math.max(0.01, value / 100) })}
            />
          </Labeled>
        </>
      )}
      {STRENGTH_TOOLS.has(tool) && (
        <Labeled label="Strength">
          <NumberInput
            value={percent(options.strength)}
            min={1}
            max={100}
            width={48}
            onChange={(value) => patch(tool, { strength: value / 100 })}
          />
        </Labeled>
      )}
      {EXPOSURE_TOOLS.has(tool) && (
        <>
          <Labeled label="Exposure">
            <NumberInput
              value={percent(options.exposure)}
              min={1}
              max={100}
              width={48}
              onChange={(value) => patch(tool, { exposure: value / 100 })}
            />
          </Labeled>
          <Labeled label="Range">
            <Select
              value={options.range}
              options={TONE_RANGES}
              onChange={(range) => patch(tool, { range })}
            />
          </Labeled>
        </>
      )}
      {tool === 'sponge' && (
        <Labeled label="Mode">
          <Select
            value={options.sponge}
            options={SPONGE_MODES}
            onChange={(sponge) => patch(tool, { sponge })}
          />
        </Labeled>
      )}
      {COLOR_TOOLS.has(tool) &&
        (channelName ? (
          <span className="optionsbar__note">Painting into {channelName}</span>
        ) : (
          <Labeled label="Paint on">
            <Select value={paintTarget} options={PAINT_TARGETS} onChange={setPaintTarget} />
          </Labeled>
        ))}
      {SOURCE_TOOLS.has(tool) && (
        <>
          <Checkbox
            label="Aligned"
            checked={options.aligned}
            onChange={(aligned) => patch(tool, { aligned })}
          />
          <Checkbox
            label="Sample all layers"
            checked={options.sampleAllLayers}
            onChange={(sampleAllLayers) => patch(tool, { sampleAllLayers })}
          />
        </>
      )}
    </>
  );
}

function GradientOptionsRow() {
  const settings = useAppStore((state) => state.gradient);
  const patch = useAppStore((state) => state.patchGradient);
  const openDialog = useAppStore((state) => state.openDialog);

  return (
    <>
      <button
        type="button"
        className="optionsbar__gradient"
        title="Edit the gradient"
        style={{ backgroundImage: `linear-gradient(90deg, ${gradientToCss(settings.value)})` }}
        onClick={() => openDialog({ kind: 'gradient' })}
      />
      <Labeled label="Type">
        <Select
          value={settings.type}
          options={GRADIENT_TYPE_OPTIONS}
          onChange={(type) => patch({ type })}
        />
      </Labeled>
      <Labeled label="Mode">
        <Select
          value={settings.blendMode}
          options={BLEND_MODE_OPTIONS}
          onChange={(blendMode) => patch({ blendMode })}
        />
      </Labeled>
      <Labeled label="Opacity">
        <NumberInput
          value={Math.round(settings.opacity * 100)}
          min={1}
          max={100}
          width={48}
          onChange={(value) => patch({ opacity: value / 100 })}
        />
      </Labeled>
      <Checkbox
        label="Reverse"
        checked={settings.reverse}
        onChange={(reverse) => patch({ reverse })}
      />
      <Checkbox label="Dither" checked={settings.dither} onChange={(dither) => patch({ dither })} />
    </>
  );
}

/**
 * Numeric mirror of the transform box, plus the reference-point grid. The box
 * lives in the tool rather than the store, so this row listens to it directly.
 */
function TransformOptionsRow() {
  const options = useAppStore((state) => state.toolOptions);
  const patch = useAppStore((state) => state.patchToolOptions);
  const anchor = options.transformAnchor;
  useSyncExternalStore(subscribeTransform, transformRevision);
  const values = transformValues(anchor);
  const warp = warpSettings();

  const set = (change: Partial<TransformValues>) => applyTransformValues(anchor, change);
  const percent = (value: number): number => Math.round(value * 1000) / 10;

  const modeSelect = (
    <Labeled label="Mode">
      <Select
        value={options.transformMode}
        options={TRANSFORM_MODE_OPTIONS}
        onChange={setTransformMode}
      />
    </Labeled>
  );

  if (warp) {
    return (
      <>
        {modeSelect}
        <Labeled label="Warp">
          <Select
            value={warp.preset}
            options={WARP_PRESET_OPTIONS}
            onChange={(preset) => setWarpPreset(preset, warp.bend || 0.4)}
          />
        </Labeled>
        <Labeled label="Bend">
          <NumberInput
            value={Math.round(warp.bend * 100)}
            disabled={warp.preset === 'custom'}
            min={-100}
            max={100}
            width={56}
            onChange={(value) => setWarpPreset(warp.preset, value / 100)}
          />
        </Labeled>
        <button type="button" className="optionsbar__button" onClick={cancelTransformSession}>
          Cancel
        </button>
        <button type="button" className="optionsbar__button" onClick={commitTransformSession}>
          Apply
        </button>
      </>
    );
  }

  return (
    <>
      {modeSelect}
      <Labeled label="Ref">
        <span className="anchorgrid" role="radiogroup" aria-label="Reference point">
          {TRANSFORM_ANCHORS.map((cell) => (
            <button
              key={cell.id}
              type="button"
              role="radio"
              aria-checked={cell.id === anchor}
              aria-label={cell.id}
              className={`anchorgrid__cell${cell.id === anchor ? ' is-active' : ''}`}
              onClick={() => {
                patch({ transformAnchor: cell.id });
                syncTransformPivot(cell.id);
              }}
            />
          ))}
        </span>
      </Labeled>
      <Labeled label="X">
        <NumberInput
          value={values ? Math.round(values.x) : 0}
          disabled={!values}
          min={-100000}
          max={100000}
          width={56}
          onChange={(x) => set({ x })}
        />
      </Labeled>
      <Labeled label="Y">
        <NumberInput
          value={values ? Math.round(values.y) : 0}
          disabled={!values}
          min={-100000}
          max={100000}
          width={56}
          onChange={(y) => set({ y })}
        />
      </Labeled>
      <Labeled label="W %">
        <NumberInput
          value={values ? percent(values.scaleX) : 100}
          disabled={!values}
          min={-2000}
          max={2000}
          step={0.1}
          width={56}
          onChange={(value) => {
            const scaleX = value / 100;
            set(
              options.transformKeepAspect && values
                ? { scaleX, scaleY: values.scaleY * (scaleX / (values.scaleX || 1)) }
                : { scaleX },
            );
          }}
        />
      </Labeled>
      <Labeled label="H %">
        <NumberInput
          value={values ? percent(values.scaleY) : 100}
          disabled={!values}
          min={-2000}
          max={2000}
          step={0.1}
          width={56}
          onChange={(value) => {
            const scaleY = value / 100;
            set(
              options.transformKeepAspect && values
                ? { scaleY, scaleX: values.scaleX * (scaleY / (values.scaleY || 1)) }
                : { scaleY },
            );
          }}
        />
      </Labeled>
      <Labeled label="Angle">
        <NumberInput
          value={values ? Math.round(values.angle * 10) / 10 : 0}
          disabled={!values}
          min={-360}
          max={360}
          step={0.1}
          width={56}
          onChange={(angle) => set({ angle })}
        />
      </Labeled>
      <Labeled label="Skew">
        <NumberInput
          value={values ? Math.round(values.skew * 10) / 10 : 0}
          disabled={!values}
          min={-89}
          max={89}
          step={0.1}
          width={56}
          onChange={(skew) => set({ skew })}
        />
      </Labeled>
      <Checkbox
        label="Keep ratio"
        checked={options.transformKeepAspect}
        onChange={(transformKeepAspect) => patch({ transformKeepAspect })}
      />
      <button
        type="button"
        className="optionsbar__button"
        disabled={!values}
        onClick={cancelTransformSession}
      >
        Cancel
      </button>
      <button
        type="button"
        className="optionsbar__button"
        disabled={!values}
        onClick={commitTransformSession}
      >
        Apply
      </button>
    </>
  );
}

function TypeOptionsRow() {
  const options = useAppStore((state) => state.toolOptions);
  const patch = useAppStore((state) => state.patchToolOptions);
  const setForeground = useAppStore((state) => state.setForeground);
  const foreground = useAppStore((state) => state.foreground);
  const doc = useAppStore(getActiveDocument);
  const session = useSyncExternalStore(subscribeTextEdit, getTextEditSession, getTextEditSession);
  const editing =
    session && doc ? findLayerById(doc.layers, session.layerId) : null;
  const active =
    !editing && doc?.activeLayerId ? findLayerById(doc.layers, doc.activeLayerId) : null;
  const text =
    editing?.kind === 'text' ? editing : active?.kind === 'text' ? active : null;

  const fontFamily = text?.fontFamily ?? options.textFontFamily;
  const fontSize = text?.fontSize ?? options.textFontSize;
  const align = text?.align ?? options.textAlign;
  const bold = text ? text.fontWeight === 'bold' : options.textBold;
  const italic = text ? text.fontStyle === 'italic' : options.textItalic;
  const color = text?.color ?? foreground;

  const apply = (patchLayer: {
    fontFamily?: string;
    fontSize?: number;
    align?: TextAlign;
    fontWeight?: 'normal' | 'bold';
    fontStyle?: 'normal' | 'italic';
    color?: RGBA;
  }) => {
    patch({
      ...(patchLayer.fontFamily !== undefined
        ? { textFontFamily: patchLayer.fontFamily }
        : {}),
      ...(patchLayer.fontSize !== undefined ? { textFontSize: patchLayer.fontSize } : {}),
      ...(patchLayer.align !== undefined ? { textAlign: patchLayer.align } : {}),
      ...(patchLayer.fontWeight !== undefined
        ? { textBold: patchLayer.fontWeight === 'bold' }
        : {}),
      ...(patchLayer.fontStyle !== undefined
        ? { textItalic: patchLayer.fontStyle === 'italic' }
        : {}),
    });
    if (patchLayer.color) setForeground(patchLayer.color);
    if (!text) return;
    if (session) updateTextTransient(patchLayer);
    else setTextLayerProps(text.id, patchLayer);
  };

  const fonts = TEXT_FONT_OPTIONS.some((f) => f.value === fontFamily)
    ? TEXT_FONT_OPTIONS
    : [{ value: fontFamily, label: fontFamily }, ...TEXT_FONT_OPTIONS];

  return (
    <>
      <Labeled label="Font">
        <Select
          value={fontFamily}
          options={fonts}
          onChange={(next) => apply({ fontFamily: next })}
        />
      </Labeled>
      <Labeled label="Size">
        <NumberInput
          value={Math.round(fontSize)}
          min={4}
          max={1000}
          width={56}
          onChange={(next) => apply({ fontSize: next })}
        />
      </Labeled>
      <Labeled label="Color">
        <label
          className="toolbar__swatch"
          title="Text color"
          style={{
            position: 'relative',
            width: 22,
            height: 22,
            background: rgbaToCss(color),
          }}
        >
          <input
            type="color"
            style={{ opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
            value={rgbaToHex(color)}
            onChange={(event) => apply({ color: hexToRgba(event.target.value) })}
          />
        </label>
      </Labeled>
      <Labeled label="Align">
        <Select
          value={align}
          options={TEXT_ALIGN_OPTIONS}
          onChange={(next) => apply({ align: next })}
        />
      </Labeled>
      <Checkbox
        label="Bold"
        checked={bold}
        onChange={(next) => apply({ fontWeight: next ? 'bold' : 'normal' })}
      />
      <Checkbox
        label="Italic"
        checked={italic}
        onChange={(next) => apply({ fontStyle: next ? 'italic' : 'normal' })}
      />
    </>
  );
}

export function ToolOptionsBar() {
  const tool = useAppStore((state) => state.tool);
  const options = useAppStore((state) => state.toolOptions);
  const patch = useAppStore((state) => state.patchToolOptions);
  const doc = useAppStore(getActiveDocument);
  const session = useSyncExternalStore(subscribeTextEdit, getTextEditSession, getTextEditSession);
  const activeLayer = doc?.activeLayerId
    ? findLayerById(doc.layers, doc.activeLayerId)
    : null;
  const showTypeOptions =
    tool === 'type' || activeLayer?.kind === 'text' || session !== null;

  return (
    <div className="optionsbar">
      <span className="optionsbar__title">{TOOL_LABELS[tool]}</span>

      <PaintOptionsRow />

      {showTypeOptions && <TypeOptionsRow />}

      {tool === 'gradient' && <GradientOptionsRow />}

      {tool === 'bucket' && (
        <>
          <Labeled label="Tolerance">
            <NumberInput
              value={Math.round(options.bucketTolerance * 100)}
              min={0}
              max={100}
              onChange={(value) => patch({ bucketTolerance: value / 100 })}
            />
          </Labeled>
          <Checkbox
            label="Contiguous"
            checked={options.bucketContiguous}
            onChange={(bucketContiguous) => patch({ bucketContiguous })}
          />
        </>
      )}

      {SELECTION_TOOLS.has(tool) && (
        <>
          <Labeled label="Mode">
            <Select
              value={options.selectionMode}
              options={SELECTION_MODES}
              onChange={(selectionMode) => patch({ selectionMode })}
            />
          </Labeled>
          <Labeled label="Feather">
            <NumberInput
              value={options.selectionFeather}
              min={0}
              max={250}
              onChange={(selectionFeather) => patch({ selectionFeather })}
            />
          </Labeled>
          <Checkbox
            label="Anti-alias"
            checked={options.selectionAntialias}
            onChange={(selectionAntialias) => patch({ selectionAntialias })}
          />
        </>
      )}

      {(tool === 'magicWand' || tool === 'quickSelect') && (
        <>
          <Labeled label="Tolerance">
            <NumberInput
              value={Math.round(options.wandTolerance * 100)}
              min={0}
              max={100}
              onChange={(value) => patch({ wandTolerance: value / 100 })}
            />
          </Labeled>
          {tool === 'magicWand' && (
            <Checkbox
              label="Contiguous"
              checked={options.wandContiguous}
              onChange={(wandContiguous) => patch({ wandContiguous })}
            />
          )}
          {tool === 'magicWand' && (
            <Checkbox
              label="Sample all layers"
              checked={options.wandSampleAllLayers}
              onChange={(wandSampleAllLayers) => patch({ wandSampleAllLayers })}
            />
          )}
          {tool === 'quickSelect' && (
            <Labeled label="Size">
              <NumberInput
                value={options.quickSelectSize}
                min={1}
                max={500}
                onChange={(quickSelectSize) => patch({ quickSelectSize })}
              />
            </Labeled>
          )}
        </>
      )}

      {tool === 'lassoMagnetic' && (
        <>
          <Labeled label="Width">
            <NumberInput
              value={options.magneticWidth}
              min={1}
              max={100}
              onChange={(magneticWidth) => patch({ magneticWidth })}
            />
          </Labeled>
          <Labeled label="Contrast">
            <NumberInput
              value={Math.round(options.magneticContrast * 100)}
              min={0}
              max={100}
              onChange={(value) => patch({ magneticContrast: value / 100 })}
            />
          </Labeled>
          <Labeled label="Frequency">
            <NumberInput
              value={options.magneticFrequency}
              min={2}
              max={200}
              onChange={(magneticFrequency) => patch({ magneticFrequency })}
            />
          </Labeled>
        </>
      )}

      {VECTOR_TOOLS.has(tool) && (
        <>
          <Labeled label="Mode">
            <Select
              value={options.vectorMode}
              options={VECTOR_MODES}
              onChange={(vectorMode) => patch({ vectorMode })}
            />
          </Labeled>
          <Labeled label="Path op">
            <Select
              value={options.pathOp}
              options={PATH_OPS}
              onChange={(pathOp) => patch({ pathOp })}
            />
          </Labeled>
          <Checkbox
            label="Fill"
            checked={options.shapeFillEnabled}
            onChange={(shapeFillEnabled) => patch({ shapeFillEnabled })}
          />
          {options.vectorMode === 'shape' && (
            <Checkbox
              label="Stroke"
              checked={options.shapeStrokeEnabled}
              onChange={(shapeStrokeEnabled) => patch({ shapeStrokeEnabled })}
            />
          )}
          {(options.shapeStrokeEnabled || options.vectorMode === 'pixels') && (
            <Labeled label="Width">
              <NumberInput
                value={options.shapeStrokeWidth}
                min={1}
                max={500}
                onChange={(shapeStrokeWidth) => patch({ shapeStrokeWidth })}
              />
            </Labeled>
          )}
          {options.shapeStrokeEnabled && options.vectorMode === 'shape' && (
            <Labeled label="Align">
              <Select
                value={options.shapeStrokeAlign}
                options={STROKE_ALIGNS}
                onChange={(shapeStrokeAlign) => patch({ shapeStrokeAlign })}
              />
            </Labeled>
          )}
        </>
      )}

      {tool === 'shapeRect' && (
        <Labeled label="Radius">
          <NumberInput
            value={options.shapeRadius}
            min={0}
            max={500}
            onChange={(shapeRadius) => patch({ shapeRadius })}
          />
        </Labeled>
      )}

      {tool === 'shapePolygon' && (
        <>
          <Labeled label="Sides">
            <NumberInput
              value={options.shapeSides}
              min={3}
              max={100}
              onChange={(shapeSides) => patch({ shapeSides })}
            />
          </Labeled>
          <Labeled label="Star">
            <NumberInput
              value={Math.round(options.shapeStarRatio * 100)}
              min={10}
              max={100}
              onChange={(value) => patch({ shapeStarRatio: value / 100 })}
            />
          </Labeled>
        </>
      )}

      {tool === 'pen' && (
        <Checkbox
          label="Rubber band"
          checked={options.penRubberBand}
          onChange={(penRubberBand) => patch({ penRubberBand })}
        />
      )}

      {tool === 'penFree' && (
        <Labeled label="Tolerance">
          <NumberInput
            value={options.penTolerance}
            min={0}
            max={50}
            onChange={(penTolerance) => patch({ penTolerance })}
          />
        </Labeled>
      )}

      {tool === 'transform' && <TransformOptionsRow />}

      {tool === 'eyedropper' && (
        <Checkbox
          label="Sample all layers"
          checked={options.eyedropperFromComposite}
          onChange={(eyedropperFromComposite) => patch({ eyedropperFromComposite })}
        />
      )}

      <span className="optionsbar__hint">{TOOLS[tool]?.hint ?? ''}</span>
    </div>
  );
}

function Labeled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <span className="optionsbar__group">
      <span className="field__label">{label}</span>
      {children}
    </span>
  );
}
